import type { MowerId } from '../../content/mowers';
import { Dir, type Direction } from '../../sim/direction';

/**
 * Mowers in three-quarter view from above (art direction, owner 2026-10-03): the camera looks
 * down at an angle from the south, so you see mostly the top of the mower, a thin strip of its
 * near face, wheels peeking out under it, and the handle rising back from it. Heading east shows
 * the mower lengthwise, south its front, north its back (west mirrors east). No riders: riding
 * mowers show an empty seat and their controls. The deck is centered on the tile it is cutting.
 * Painted into a square frame (28 px, 48 for the Fairway Mower) centered on that tile, before the
 * outline and ground shadow (mowerArt.ts). Body colors come from the paint slots (0 body, 1
 * light, 2 dark) so cosmetics can recolor them.
 */
export type PaintSlot = 0 | 1 | 2;
export type BodyPaint = (slot: PaintSlot, x: number, y: number) => string;
type Put = (x: number, y: number, color: string) => void;

const K = {
  tire: '#1f2023',
  tread: '#3d4046',
  metal: '#8f969d',
  metalLit: '#c9ced3',
  metalDark: '#5d6369',
  engine: '#34383e',
  engineLit: '#646b74',
  red: '#d8432f',
  seat: '#26262b',
  seatLit: '#4a4a52',
  bag: '#4b5146',
  bagLit: '#6d7563',
  grille: '#2a2c33',
  lamp: '#f6e6a0',
} as const;

/** A reel's spinning blades: a diagonal light/mid/dark pattern. */
const blade = (x: number, y: number) =>
  [K.metalLit, K.metal, K.metalDark, K.metal][(x + y) % 4] as string;

type Facing = 'side' | 'front' | 'back';

interface Pen {
  px: Put;
  rect: (
    x: number,
    y: number,
    w: number,
    h: number,
    c: string | ((x: number, y: number) => string),
  ) => void;
  ell: (
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    fn: (x: number, y: number, d: number, ex: number, ey: number) => void,
  ) => void;
  line: (x0: number, y0: number, x1: number, y1: number, c: string) => void;
  body: (slot: PaintSlot) => (x: number, y: number) => string;
}

function pen(put: Put, paint: BodyPaint): Pen {
  return {
    px: put,
    rect: (x0, y0, w, h, c) => {
      for (let y = y0; y < y0 + h; y++) {
        for (let x = x0; x < x0 + w; x++) put(x, y, typeof c === 'string' ? c : c(x, y));
      }
    },
    ell: (cx, cy, rx, ry, fn) => {
      for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
        for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
          const ex = (x + 0.5 - cx) / rx;
          const ey = (y + 0.5 - cy) / ry;
          const d = ex * ex + ey * ey;
          if (d <= 1) fn(x, y, d, ex, ey);
        }
      }
    },
    line: (x0, y0, x1, y1, c) => {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
      for (let i = 0; i <= n; i++) {
        put(Math.round(x0 + ((x1 - x0) * i) / n), Math.round(y0 + ((y1 - y0) * i) / n), c);
      }
    },
    body: (slot) => (x, y) => paint(slot, x, y),
  };
}

/**
 * A tire seen from above: its tread (a far-side wheel's sliver peeking past the body, or a wheel
 * seen tread-on from the front or back). The lugs run across the way it rolls: columns when it is
 * wider than tall (rolling east-west), rows otherwise. Big ones get rounded corners.
 */
function wheel(p: Pen, x: number, y: number, w: number, h: number): void {
  const big = w > 2 && h > 2;
  for (let yy = y; yy < y + h; yy++) {
    for (let xx = x; xx < x + w; xx++) {
      const corner = (xx === x || xx === x + w - 1) && (yy === y || yy === y + h - 1);
      if (corner && big) continue;
      const lug = w > h ? (xx - x) % 2 === 1 : (yy - y) % 2 === 1;
      const inside = w > h ? yy > y || h === 1 : xx > x && xx < x + w - 1;
      p.px(xx, yy, lug && inside && (big || h === 1 || w <= 2) ? K.tread : K.tire);
    }
  }
}

/**
 * A small near-side wheel seen side on (5 × 4): a dark tire round a lit hub, top left at (x, y).
 * It sits over the body's near face, as wheels do seen from above and in front.
 */
function smallTire(p: Pen, x: number, y: number): void {
  const art = ['.TTT.', 'TLMTT', 'TMDTT', '.TTT.'];
  const key: Record<string, string> = { T: K.tire, L: K.metalLit, M: K.metal, D: K.metalDark };
  art.forEach((row, yy) =>
    [...row].forEach((ch, xx) => {
      if (ch !== '.') p.px(x + xx, y + yy, key[ch]!);
    }),
  );
}

/**
 * A near-side tire seen side on (from above at an angle, so a squat oval): a dark tire, its tread
 * lugs showing along the bottom where it meets the ground, a painted wheel rim (`rim`) and a hub.
 */
function tire(p: Pen, cx: number, cy: number, rx: number, ry: number, rim: string): void {
  p.ell(cx, cy, rx, ry, (x, y, d, _ex, ey) => {
    p.px(x, y, d > 0.55 && ey > 0.35 && x % 2 === 0 ? K.tread : K.tire);
  });
  if (rx < 3.5) {
    // A small wheel's rim is a short bar, lit at its left (any rounder and it reads as a cross).
    const x = Math.round(cx - 1);
    const y = Math.round(cy - 0.5);
    p.px(x, y, K.metalLit);
    p.px(x + 1, y, rim);
    return;
  }
  p.ell(cx, cy, rx * 0.5, ry * 0.5, (x, y, _d, ex, ey) =>
    p.px(x, y, ex + ey < -0.6 ? K.metalLit : rim),
  );
  p.px(Math.floor(cx), Math.floor(cy), K.metalDark);
}

/**
 * A deck from above: its top face over a strip of near face `face` rows deep. Corners rounded;
 * the top face lit along its top and left edges (the light wraps round the corner), its right
 * edge turning away into shade, its near face darker below.
 */
function deck(p: Pen, x: number, y: number, w: number, h: number, face: number): void {
  const top = h - face;
  for (let ly = 0; ly < h; ly++) {
    for (let lx = 0; lx < w; lx++) {
      const rx = w - 1 - lx;
      const edgeX = Math.min(lx, rx);
      // Rounded corners: two pixels cut at the top corners, one at the bottom ones.
      if (ly === 0 && edgeX <= 1) continue;
      if (ly === 1 && edgeX === 0) continue;
      if (ly === h - 1 && edgeX === 0) continue;
      let c: (x: number, y: number) => string;
      if (ly >= top) c = p.body(2);
      else if (ly <= 1 || lx <= 1)
        c = ly === 0 || lx === 0 || (ly === 1 && lx === 1) ? p.body(1) : p.body(0);
      else if (rx === 0) c = p.body(2);
      else c = p.body(0);
      p.px(x + lx, y + ly, c(x + lx, y + ly));
    }
  }
  // A broad soft highlight across the top face's lit half.
  for (let lx = 2; lx < Math.floor(w * 0.45); lx++) p.px(x + lx, y + 1, p.body(1)(x + lx, y + 1));
}

/** An engine housing on top: a round dome, lit top left, darker at its foot, a red cap. */
function engine(p: Pen, cx: number, cy: number, rx: number, ry: number): void {
  p.ell(cx, cy + 1, rx, ry, (x, y) => p.px(x, y, K.metalDark));
  p.ell(cx, cy, rx, ry, (x, y, d, ex, ey) => {
    p.px(x, y, ex + ey < -0.45 ? K.metalLit : d > 0.75 ? K.metalDark : K.metal);
  });
  p.rect(Math.round(cx) - 1, Math.round(cy) - 1, 2, 2, K.red);
}

// ── Walk-behind mowers ──────────────────────────────────────────────────────────────────────

/** The push mower (and with `bag`, the self-propelled one): handle, deck, engine. */
function paintWalkBehind(p: Pen, f: Facing, bag: boolean): void {
  if (f === 'side') {
    // Heading east: the far wheels just a sliver of tread past the deck, the handle rising back
    // west, the near wheels side on over the deck's near face.
    wheel(p, 8, 8, 4, 1);
    wheel(p, 18, 8, 4, 1);
    if (bag) {
      p.rect(1, 9, 6, 9, K.bag);
      p.rect(1, 9, 6, 1, K.bagLit);
      for (let y = 11; y < 18; y += 2) p.rect(2, y, 4, 1, K.bagLit);
    }
    p.line(7, 10, 1, 5, K.metalDark);
    p.line(7, 16, 1, 11, K.metal);
    p.rect(0, 5, 2, 7, K.metalDark);
    p.rect(0, 7, 2, 3, K.red);
    deck(p, 6, 9, 17, 10, 2);
    engine(p, 15, 13, 4, 3.2);
    smallTire(p, 7, 17);
    smallTire(p, 17, 17);
    return;
  }
  if (f === 'front') {
    // Heading south: the handle rises behind (north) to its grip, the front face toward us.
    p.line(9, 8, 9, 2, K.metalDark);
    p.line(19, 8, 19, 2, K.metalDark);
    p.rect(9, 1, 11, 2, K.metalDark);
    p.rect(12, 1, 5, 2, K.red);
    if (bag) {
      p.rect(10, 3, 9, 5, K.bag);
      p.rect(10, 3, 9, 1, K.bagLit);
    }
    wheel(p, 6, 8, 2, 3);
    wheel(p, 21, 8, 2, 3);
    deck(p, 7, 8, 15, 11, 2);
    wheel(p, 6, 16, 2, 4);
    wheel(p, 21, 16, 2, 4);
    engine(p, 14.5, 12.5, 3.8, 3);
    return;
  }
  // Heading north: the deck from behind, the handle (and bag) coming back toward us.
  wheel(p, 6, 7, 2, 3);
  wheel(p, 21, 7, 2, 3);
  deck(p, 7, 7, 15, 11, 2);
  wheel(p, 6, 15, 2, 4);
  wheel(p, 21, 15, 2, 4);
  engine(p, 14.5, 11.5, 3.8, 3);
  if (bag) {
    p.rect(10, 17, 9, 6, K.bag);
    p.rect(10, 17, 9, 1, K.bagLit);
    for (let y = 19; y < 23; y += 2) p.rect(11, y, 7, 1, K.bagLit);
  }
  p.line(9, 17, 9, 24, K.metal);
  p.line(19, 17, 19, 24, K.metal);
  p.rect(9, 24, 11, 2, K.metalDark);
  p.rect(12, 24, 5, 2, K.red);
}

/** The reel mower: a cylinder of blades between two wheels, a small roller, the handle. */
function paintReel(p: Pen, f: Facing): void {
  if (f === 'side') {
    // Heading east: the reel cylinder across the front between its two wheels (the far one a
    // sliver of tread past it, the near one side on with its red hub), the roller behind it, the
    // handle rising back west to its grip.
    wheel(p, 12, 6, 8, 1);
    p.line(11, 9, 2, 4, K.metalDark);
    p.line(11, 17, 2, 10, K.metal);
    p.rect(1, 4, 2, 7, K.metalDark);
    p.rect(1, 6, 2, 3, K.red);
    p.rect(9, 9, 2, 9, K.metalDark);
    p.rect(9, 9, 1, 9, K.metal);
    // The reel: spiral blades as diagonal stripes, lit on its left, the bed knife along its front.
    p.rect(12, 7, 8, 12, (x, y) => {
      const c = [K.metalLit, K.metal, K.metalDark, K.metal][(((x - y) % 4) + 4) % 4]!;
      if (x === 12) return c === K.metalDark ? K.metal : K.metalLit;
      if (x === 19) return c === K.metalLit ? K.metal : K.metalDark;
      return c;
    });
    p.rect(12, 7, 8, 1, K.metalLit);
    p.rect(20, 8, 1, 10, K.engine);
    tire(p, 16, 20.5, 4.6, 3.3, p.body(0)(0, 0));
    return;
  }
  const behind = f === 'front';
  if (behind) {
    p.line(10, 9, 10, 2, K.metalDark);
    p.line(18, 9, 18, 2, K.metalDark);
    p.rect(10, 1, 9, 2, K.metalDark);
    p.rect(12, 1, 5, 2, K.red);
  }
  // The reel across, its wheels at either end (red plates), the roller behind.
  const ry = behind ? 13 : 9;
  p.rect(9, behind ? 9 : 17, 11, 2, K.metalDark);
  p.rect(8, ry, 13, 6, blade);
  p.rect(8, ry, 13, 1, K.metalLit);
  for (const x of [5, 21]) {
    p.rect(x, ry - 3, 3, 11, K.tire);
    p.rect(x + 1, ry - 1, 1, 7, p.body(0));
  }
  if (!behind) {
    p.line(10, 18, 10, 24, K.metal);
    p.line(18, 18, 18, 24, K.metal);
    p.rect(10, 24, 9, 2, K.metalDark);
    p.rect(12, 24, 5, 2, K.red);
  }
}

// ── Riding mowers (no rider: an empty seat and the controls) ────────────────────────────────

/**
 * The lawn tractor: a long hood with a grille and headlights out front, fenders over big lugged
 * rear tires, small front tires, a high-backed seat behind the steering wheel, and the cutting
 * deck slung underneath between the wheels with its discharge chute.
 */
function paintTractor(p: Pen, f: Facing): void {
  const deckUnder = (x: number, y: number, w: number, h: number) => {
    p.rect(x, y, w, h, K.metalDark);
    p.rect(x, y, w, 1, K.metal);
  };
  if (f === 'side') {
    // Heading east: rear west, nose east. Far wheels peek above the body.
    wheel(p, 3, 6, 9, 2);
    wheel(p, 19, 9, 5, 1);
    deckUnder(9, 17, 12, 4);
    p.rect(13, 20, 4, 2, K.engine); // the chute, toward us
    // The chassis: rear fenders and seat pod, then the long hood.
    p.rect(2, 8, 11, 10, p.body(0));
    p.rect(2, 8, 11, 1, p.body(1));
    p.rect(2, 8, 1, 10, p.body(1));
    p.rect(2, 16, 11, 2, p.body(2));
    p.rect(13, 10, 11, 7, p.body(0));
    p.rect(13, 10, 11, 1, p.body(1));
    p.rect(14, 12, 9, 1, p.body(1)); // a crease along the hood
    p.rect(13, 15, 11, 2, p.body(2));
    p.rect(24, 11, 1, 5, K.grille);
    p.px(24, 11, K.lamp);
    p.px(24, 14, K.lamp);
    // The seat, its high back toward the rear, and the steering wheel on its column.
    p.rect(3, 6, 2, 9, K.seat);
    p.rect(3, 6, 2, 1, K.seatLit);
    p.rect(5, 10, 5, 5, K.seat);
    p.rect(5, 10, 5, 1, K.seatLit);
    p.rect(12, 10, 1, 3, K.metalDark);
    p.ell(12, 9.5, 1.8, 1.4, (x, y, d) => {
      if (d > 0.3) p.px(x, y, K.tire);
    });
    tire(p, 6.5, 19.5, 4.6, 3.4, p.body(1)(0, 0));
    tire(p, 20.5, 19.5, 3.2, 2.6, p.body(1)(0, 0));
    return;
  }
  if (f === 'front') {
    // Heading south (as wide as the side view is deep): the seat at the back between the rear
    // tires, the hood coming toward us to its grille, small front tires either side of it.
    wheel(p, 6, 3, 4, 10);
    wheel(p, 18, 3, 4, 10);
    deckUnder(7, 13, 14, 4);
    p.rect(5, 13, 2, 3, K.engine); // the chute out on its right (our left, heading south)
    p.rect(6, 3, 4, 3, p.body(1)); // fenders
    p.rect(18, 3, 4, 3, p.body(0));
    p.rect(10, 4, 8, 8, p.body(0));
    p.rect(10, 4, 8, 1, p.body(1));
    p.rect(11, 2, 6, 2, K.seat); // the high back
    p.rect(11, 2, 6, 1, K.seatLit);
    p.rect(11, 4, 6, 4, K.seat);
    p.rect(11, 4, 6, 1, K.seatLit);
    // The dash and the steering wheel, a thin ring on its column.
    p.rect(10, 10, 8, 2, p.body(2));
    p.rect(14, 9, 1, 2, K.metalDark);
    p.ell(14, 8.5, 2, 1, (x, y, d) => {
      if (d > 0.4) p.px(x, y, K.metalDark);
    });
    p.rect(10, 12, 8, 8, p.body(0));
    p.rect(10, 12, 1, 8, p.body(1));
    p.rect(13, 12, 2, 8, p.body(1)); // the crease down the hood
    p.rect(17, 12, 1, 8, p.body(2));
    p.rect(10, 20, 8, 3, K.grille);
    p.rect(11, 20, 2, 1, K.lamp);
    p.rect(15, 20, 2, 1, K.lamp);
    p.rect(10, 23, 8, 1, K.metalDark);
    wheel(p, 8, 17, 2, 6);
    wheel(p, 18, 17, 2, 6);
    return;
  }
  // Heading north: the hood away from us, the seat's back nearest, big rear tires.
  wheel(p, 8, 3, 2, 5);
  wheel(p, 18, 3, 2, 5);
  deckUnder(7, 9, 14, 4);
  p.rect(21, 9, 2, 3, K.engine);
  p.rect(10, 2, 8, 10, p.body(0));
  p.rect(10, 2, 8, 1, p.body(1));
  p.rect(10, 2, 1, 10, p.body(1));
  p.rect(13, 2, 2, 9, p.body(1));
  p.rect(17, 2, 1, 10, p.body(2));
  p.ell(14, 12, 2, 1, (x, y, d) => {
    if (d > 0.4) p.px(x, y, K.metalDark);
  });
  p.rect(7, 13, 14, 7, p.body(0));
  p.rect(7, 13, 14, 1, p.body(1));
  p.rect(11, 14, 6, 4, K.seat);
  p.rect(11, 14, 6, 1, K.seatLit);
  p.rect(10, 17, 8, 3, K.seat);
  p.rect(10, 17, 8, 1, K.seatLit);
  p.rect(7, 20, 14, 2, p.body(2));
  p.px(8, 20, K.red);
  p.px(19, 20, K.red);
  p.rect(13, 22, 2, 1, K.metalDark); // the hitch
  wheel(p, 5, 13, 3, 11);
  wheel(p, 20, 13, 3, 11);
  p.rect(5, 13, 3, 2, p.body(1));
  p.rect(20, 13, 3, 2, p.body(0));
}

/**
 * The zero-turn: big drive tires either side of the engine at the back, a high-backed seat in
 * front of it with a lap bar on each side, a footplate, small caster wheels at the front corners,
 * and a wide deck out front that sticks out past everything, its chute on the right.
 */
function paintZeroTurn(p: Pen, f: Facing): void {
  const plate = (x: number, y: number, w: number, h: number) =>
    p.rect(x, y, w, h, (xx, yy) => ((xx * 2 + yy) % 5 === 0 ? K.metal : K.metalDark));
  const engineBlock = (x: number, y: number, w: number, h: number) => {
    p.rect(x, y, w, h, K.engine);
    p.rect(x, y, w, 1, K.engineLit);
    for (let yy = y + 2; yy < y + h; yy += 2) p.rect(x + 1, yy, w - 2, 1, K.engineLit);
  };
  if (f === 'side') {
    // Heading east: engine and drive tires west, deck and casters east.
    wheel(p, 2, 6, 9, 2);
    wheel(p, 21, 8, 3, 1);
    // The deck: wide, low, its top showing on our side of the frame, the chute toward us.
    deck(p, 9, 14, 16, 7, 2);
    p.rect(15, 19, 4, 3, K.engine);
    // The frame and footplate, the seat, the engine behind it.
    p.rect(8, 9, 15, 5, K.metalDark);
    plate(14, 10, 8, 4);
    engineBlock(1, 8, 5, 9);
    p.rect(2, 6, 2, 2, K.metal); // the air filter
    p.rect(5, 4, 2, 11, K.seat);
    p.rect(5, 4, 2, 1, K.seatLit);
    p.rect(7, 9, 5, 5, K.seat);
    p.rect(7, 9, 5, 1, K.seatLit);
    // A lap bar, its foam grip folded in.
    p.rect(12, 8, 1, 7, K.metalDark);
    p.rect(11, 7, 3, 1, K.tire);
    // Casters up front on their forks, the big drive tire nearest.
    p.rect(22, 15, 1, 4, K.metal);
    tire(p, 22, 20.5, 1.9, 1.8, K.metal);
    tire(p, 6, 19.5, 5, 3.6, p.body(1)(0, 0));
    return;
  }
  if (f === 'front') {
    // Heading south (as wide as the side view is deep): drive tires and engine at the back, the
    // deck across the front.
    wheel(p, 5, 2, 3, 11);
    wheel(p, 20, 2, 3, 11);
    engineBlock(9, 1, 10, 4);
    p.rect(16, 0, 2, 2, K.metal);
    p.rect(10, 3, 8, 3, K.seat);
    p.rect(10, 3, 8, 1, K.seatLit);
    p.rect(11, 6, 6, 4, K.seat);
    p.rect(11, 6, 6, 1, K.seatLit);
    for (const x of [8, 19]) p.rect(x, 6, 1, 6, K.metalDark);
    p.rect(8, 6, 3, 1, K.tire);
    p.rect(17, 6, 3, 1, K.tire);
    plate(10, 10, 8, 3);
    deck(p, 5, 13, 18, 8, 2);
    p.rect(7, 16, 14, 1, p.body(1)); // a stripe along the deck
    p.rect(3, 14, 2, 4, K.engine); // the chute on its right (our left, heading south)
    for (const x of [6, 19]) {
      p.rect(x + 1, 18, 1, 2, K.metal);
      wheel(p, x, 20, 3, 3);
    }
    return;
  }
  // Heading north: casters and deck away from us, the seat's back and the engine nearest.
  for (const x of [6, 19]) wheel(p, x, 2, 3, 3);
  deck(p, 5, 4, 18, 8, 2);
  p.rect(7, 7, 14, 1, p.body(1));
  p.rect(23, 5, 2, 4, K.engine);
  plate(10, 9, 8, 3);
  for (const x of [8, 19]) p.rect(x, 9, 1, 6, K.metalDark);
  p.rect(8, 9, 3, 1, K.tire);
  p.rect(17, 9, 3, 1, K.tire);
  p.rect(11, 12, 6, 3, K.seat);
  p.rect(11, 12, 6, 1, K.seatLit);
  p.rect(10, 15, 8, 4, K.seat);
  p.rect(10, 15, 8, 1, K.seatLit);
  wheel(p, 5, 12, 3, 12);
  wheel(p, 20, 12, 3, 12);
  engineBlock(8, 19, 12, 5);
  p.rect(16, 18, 2, 2, K.metal);
}

/**
 * The Fairway Mower (48 px frame, its tile in the middle): a cream riding unit with big front
 * tires, the engine at the back under a vented hood, a roll bar over the empty seat, and a gang of
 * reels out front across all three rows it cuts (the middle one a little ahead of the wings), with
 * two more slung under its belly between the wheels.
 */
function paintFairway(p: Pen, f: Facing): void {
  // A reel unit: its blades, a roller behind and in front, cream end plates.
  const reel = (x: number, y: number, w: number, h: number, across: boolean) => {
    p.rect(x, y, w, h, blade);
    if (across) {
      p.rect(x, y, w, 1, K.metalDark);
      p.rect(x, y + h - 1, w, 1, K.metal);
      p.rect(x - 1, y - 1, 2, h + 1, p.body(1));
      p.rect(x + w - 1, y - 1, 2, h + 1, p.body(2));
    } else {
      p.rect(x, y, 1, h, K.metalDark);
      p.rect(x + w - 1, y, 1, h, K.metal);
      p.rect(x - 1, y - 1, w + 1, 2, p.body(1));
      p.rect(x - 1, y + h - 1, w + 1, 2, p.body(2));
    }
  };
  const ring = (cx: number, cy: number, rx: number, ry: number) =>
    p.ell(cx, cy, rx, ry, (x, y, d) => {
      if (d > 0.3) p.px(x, y, K.tire);
    });
  const hood = (x: number, y: number, w: number, h: number, vertical: boolean) => {
    p.rect(x, y, w, h, p.body(1));
    if (vertical)
      for (let xx = x + 2; xx < x + w - 1; xx += 2) p.rect(xx, y + 2, 1, h - 4, p.body(2));
    else for (let yy = y + 2; yy < y + h - 1; yy += 2) p.rect(x + 2, yy, w - 4, 1, p.body(2));
  };
  if (f === 'side') {
    // Heading east: rear west, reels east, the far side's wheels and belly reel peeking above.
    wheel(p, 8, 13, 7, 3);
    wheel(p, 24, 12, 8, 4);
    reel(18, 9, 5, 6, false);
    for (const [y0, y1] of [
      [19, 11],
      [24, 24],
      [29, 37],
    ] as const)
      p.line(31, y0, 35, y1, K.metalDark);
    deck(p, 6, 16, 26, 16, 3);
    hood(6, 17, 10, 11, false);
    p.rect(17, 19, 2, 9, K.seat);
    p.rect(17, 19, 2, 1, K.seatLit);
    p.rect(19, 20, 5, 7, K.seat);
    p.rect(19, 20, 5, 1, K.seatLit);
    // The roll bar behind the seat, rising.
    p.rect(15, 9, 2, 20, K.engine);
    p.rect(15, 9, 2, 1, K.engineLit);
    p.rect(27, 21, 1, 4, K.metalDark);
    ring(27, 20.5, 1.8, 2.2);
    p.rect(32, 19, 1, 9, K.grille);
    reel(18, 33, 5, 6, false);
    tire(p, 11.5, 33.5, 4, 3, p.body(2)(0, 0));
    tire(p, 27.5, 33, 4.8, 3.6, p.body(2)(0, 0));
    reel(34, 4, 5, 13, false);
    reel(34, 31, 5, 13, false);
    reel(38, 17, 5, 14, false);
    return;
  }
  if (f === 'front') {
    // Heading south: the engine hood at the back, the roll bar, the seat, the reels toward us.
    wheel(p, 11, 6, 4, 8);
    wheel(p, 33, 6, 4, 8);
    reel(5, 18, 9, 4, true);
    reel(34, 18, 9, 4, true);
    deck(p, 13, 6, 22, 24, 3);
    hood(15, 6, 18, 8, true);
    p.rect(19, 15, 10, 2, K.seat);
    p.rect(19, 15, 10, 1, K.seatLit);
    p.rect(20, 17, 8, 5, K.seat);
    p.rect(20, 17, 8, 1, K.seatLit);
    p.rect(13, 3, 2, 12, K.engine);
    p.rect(33, 3, 2, 12, K.engine);
    p.rect(13, 3, 22, 2, K.engine);
    p.rect(13, 3, 22, 1, K.engineLit);
    p.rect(24, 24, 1, 3, K.metalDark);
    ring(24, 23.5, 3, 1.6);
    p.rect(17, 30, 14, 2, K.grille);
    p.px(18, 30, K.lamp);
    p.px(29, 30, K.lamp);
    wheel(p, 9, 21, 5, 11);
    wheel(p, 34, 21, 5, 11);
    for (const [x0, x1] of [
      [17, 11],
      [24, 24],
      [31, 37],
    ] as const)
      p.line(x0, 32, x1, 35, K.metalDark);
    reel(4, 35, 12, 5, true);
    reel(32, 35, 12, 5, true);
    reel(18, 39, 12, 5, true);
    return;
  }
  // Heading north: the reels away from us, the seat, the roll bar and the engine hood nearest.
  reel(18, 4, 12, 5, true);
  reel(4, 8, 12, 5, true);
  reel(32, 8, 12, 5, true);
  for (const [x0, x1] of [
    [17, 11],
    [24, 24],
    [31, 37],
  ] as const)
    p.line(x1, 13, x0, 17, K.metalDark);
  wheel(p, 9, 15, 5, 10);
  wheel(p, 34, 15, 5, 10);
  reel(5, 25, 9, 4, true);
  reel(34, 25, 9, 4, true);
  deck(p, 13, 16, 22, 24, 3);
  p.rect(24, 19, 1, 3, K.metalDark);
  ring(24, 19.5, 3, 1.6);
  p.rect(20, 22, 8, 5, K.seat);
  p.rect(20, 22, 8, 1, K.seatLit);
  p.rect(19, 27, 10, 2, K.seat);
  p.rect(19, 27, 10, 1, K.seatLit);
  hood(15, 29, 18, 9, true);
  p.rect(13, 20, 2, 12, K.engine);
  p.rect(33, 20, 2, 12, K.engine);
  p.rect(13, 20, 22, 2, K.engine);
  p.rect(13, 20, 22, 1, K.engineLit);
  wheel(p, 11, 32, 4, 9);
  wheel(p, 33, 32, 4, 9);
}

// ── Wrecks ─────────────────────────────────────────────────────────────────────────────────

/** Small deterministic hash for soot and scorch (textures stay stable). */
function hash(x: number, y: number, salt: number): number {
  let h = Math.imul(x * 374761393 + y * 668265263 + salt * 2147483647, 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1103515245);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** A color dulled by the crash: toward gray and darker (it keeps its hue, so the mower still reads). */
function dulled(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const rgb = [n >> 16, (n >> 8) & 0xff, n & 0xff];
  const gray = 0.3 * rgb[0]! + 0.59 * rgb[1]! + 0.11 * rgb[2]!;
  return `#${rgb
    .map((c) => Math.round((c * 0.55 + gray * 0.45) * 0.78))
    .map((c) => c.toString(16).padStart(2, '0'))
    .join('')}`;
}

/** Where the wreck lies: what is drawn on the ground (scorch, spills) and the smoke above it. */
export interface WreckLayers {
  ground: Put;
  smoke: Put;
}

const SCORCH = 'rgba(24, 18, 12, 0.42)';
const OIL = 'rgba(12, 12, 16, 0.7)';
const SMOKE = ['rgba(56, 56, 62, 0.92)', 'rgba(104, 104, 112, 0.88)', 'rgba(168, 168, 176, 0.8)'];
const SOOT = '#2c2926';

/**
 * The wrecked pose: each mower busted up where it stopped, facing no way in particular (it shows
 * whatever the heading was). Its paint dulled and sooty, the body dented and cracked, a wheel
 * knocked off, parts scattered on a scorched patch of lawn, smoke rising from the engine.
 */
export function paintWreck3q(put: Put, layers: WreckLayers, id: MowerId, paint: BodyPaint): void {
  const sooty: BodyPaint = (slot, x, y) =>
    hash(x >> 1, y >> 1, 9) < 0.09 ? SOOT : dulled(paint(slot, x, y));
  const p = pen(put, sooty);
  const g = pen(layers.ground, sooty);
  const size = id === 'fairway' ? 48 : 28;
  const c = size / 2;
  // Scorched lawn under it, an oil spill, and the smoke (drawn by the caller over everything).
  g.ell(c, c + size * 0.16, size * 0.4, size * 0.16, (x, y, d) => {
    if (d < 0.75 || hash(x, y, 3) < 0.5) layers.ground(x, y, SCORCH);
  });
  g.ell(c + size * 0.18, c + size * 0.22, size * 0.08, size * 0.04, (x, y) =>
    layers.ground(x, y, OIL),
  );
  const smoke = (x0: number, y0: number, scale = 1) => {
    const puffs: [number, number, number][] = [
      [0, 0, 3],
      [2, -4, 2.6],
      [-1, -8, 2.1],
      [1, -11, 1.5],
    ];
    puffs.forEach(([dx, dy, r], k) => {
      p.ell(x0 + dx * scale, y0 + dy * scale, r * scale, r * scale * 0.9, (x, y, _d, ex, ey) => {
        if (y < 0) return;
        layers.smoke(x, y, SMOKE[Math.min(2, k === 0 ? 0 : ex + ey < -0.3 ? 2 : 1)]!);
      });
    });
  };
  const crack = (pts: [number, number][]) => {
    for (let i = 0; i + 1 < pts.length; i++) p.line(...pts[i]!, ...pts[i + 1]!, K.tire);
  };
  const bits = (pts: [number, number][]) => {
    // Loose bolts and bits lie on the lawn (not outlined: they are specks, not objects).
    pts.forEach(([x, y], i) => layers.ground(x, y, i % 2 ? K.metalLit : K.metal));
  };
  const looseTire = (cx: number, cy: number, rx: number) => {
    // A wheel knocked off, lying flat on the lawn: seen from above, a dark ring round its hub.
    p.ell(cx, cy, rx, rx * 0.6, (x, y, d) => put(x, y, d > 0.35 ? K.tire : K.metal));
    put(Math.floor(cx), Math.floor(cy), K.metalDark);
  };
  switch (id) {
    case 'push':
    case 'selfPropelled': {
      if (id === 'selfPropelled') {
        // The bag torn off and split, clippings spilling out of it.
        p.rect(17, 3, 8, 5, K.bag);
        p.rect(17, 3, 8, 1, K.bagLit);
        p.line(19, 5, 23, 7, K.tire);
        for (const [x, y] of [
          [24, 8],
          [25, 9],
          [22, 9],
          [26, 7],
        ] as const)
          layers.ground(x, y, '#7fbf4a');
      }
      wheel(p, 6, 10, 2, 3);
      wheel(p, 21, 10, 2, 3);
      // One handle bar bent back up off the deck.
      p.line(9, 10, 8, 6, K.metalDark);
      p.line(8, 6, 11, 4, K.metalDark);
      deck(p, 7, 10, 15, 9, 2);
      crack([
        [9, 12],
        [12, 14],
        [11, 16],
        [14, 17],
      ]);
      engine(p, 15.5, 13.5, 3.6, 2.8);
      p.rect(15, 13, 2, 2, K.engine); // its cap knocked off
      wheel(p, 6, 16, 2, 4);
      // The rest of the handle on the lawn, its red grip, a wheel rolled off, bolts and a spring.
      p.line(2, 23, 9, 20, K.metal);
      p.rect(1, 22, 2, 2, K.red);
      looseTire(23, 21, 2.6);
      bits([
        [4, 14],
        [25, 13],
        [12, 22],
        [3, 18],
        [4, 19],
        [3, 20],
      ]);
      smoke(15, 9);
      return;
    }
    case 'reel': {
      p.rect(5, 8, 3, 10, K.tire);
      p.rect(6, 10, 1, 6, p.body(0));
      // The reel bent out of true, some of its blades torn out.
      p.rect(8, 11, 13, 6, blade);
      p.rect(8, 11, 13, 1, K.metalLit);
      for (const [x, y] of [
        [12, 13],
        [13, 14],
        [17, 12],
        [18, 15],
      ] as const)
        put(x, y, K.tire);
      p.rect(9, 17, 11, 2, K.metalDark);
      // The handle bent over onto the lawn, a wheel rolled away, a spring sprung, shards.
      p.line(11, 18, 6, 24, K.metal);
      p.line(17, 18, 12, 25, K.metalDark);
      p.rect(5, 24, 9, 2, K.metalDark);
      p.rect(7, 24, 4, 2, K.red);
      looseTire(23, 20, 2.8);
      for (const [x, y] of [
        [14, 9],
        [15, 8],
        [14, 7],
        [15, 6],
        [14, 5],
      ] as const)
        put(x, y, K.metalLit);
      bits([
        [3, 21],
        [24, 9],
        [20, 24],
      ]);
      return;
    }
    case 'tractor': {
      wheel(p, 4, 9, 3, 5);
      wheel(p, 21, 9, 3, 5);
      deck(p, 6, 9, 16, 11, 2);
      // The hood sprung open (its underside toward us), the engine bared under it.
      p.rect(9, 9, 10, 5, K.engine);
      for (let x = 10; x < 18; x += 2) p.rect(x, 10, 1, 3, K.engineLit);
      p.rect(8, 3, 12, 5, p.body(1));
      p.rect(8, 7, 12, 1, p.body(2));
      p.rect(9, 4, 10, 2, p.body(0));
      p.line(9, 8, 10, 9, K.metalDark);
      p.line(18, 8, 17, 9, K.metalDark);
      // The seat knocked askew, a crack across the body, a front wheel still on.
      p.rect(11, 15, 5, 3, K.seat);
      p.rect(12, 14, 4, 1, K.seatLit);
      crack([
        [7, 16],
        [9, 17],
        [8, 18],
      ]);
      wheel(p, 21, 16, 3, 5);
      // A big rear tire off on its side beside it, the steering wheel on the lawn, bolts.
      tire(p, 5, 21, 4, 3, p.body(1)(0, 0));
      p.ell(23.5, 23.5, 2, 1, (x, y, d) => {
        if (d > 0.3) put(x, y, K.tire);
      });
      bits([
        [14, 22],
        [25, 7],
        [2, 10],
      ]);
      smoke(14, 7);
      return;
    }
    case 'zeroTurn': {
      wheel(p, 2, 8, 3, 10);
      wheel(p, 23, 10, 3, 8);
      // The engine block and seat, a lap bar bent up, over a cracked deck.
      p.rect(9, 6, 10, 6, K.engine);
      p.rect(9, 6, 10, 1, K.engineLit);
      for (let y = 8; y < 12; y += 2) p.rect(10, y, 8, 1, K.engineLit);
      p.line(8, 12, 5, 6, K.metalDark);
      p.rect(4, 5, 3, 1, K.tire);
      deck(p, 4, 12, 20, 8, 2);
      crack([
        [16, 13],
        [18, 15],
        [17, 17],
        [20, 18],
      ]);
      p.rect(10, 12, 7, 3, K.seat);
      p.rect(10, 12, 7, 1, K.seatLit);
      // The chute hanging off, the other lap bar and a caster on the lawn.
      p.line(24, 14, 26, 18, K.engine);
      p.line(25, 14, 27, 17, K.engine);
      p.line(18, 23, 24, 25, K.metalDark);
      p.rect(23, 24, 3, 1, K.tire);
      looseTire(5, 22, 2);
      bits([
        [12, 23],
        [25, 7],
      ]);
      smoke(14, 5);
      return;
    }
    case 'fairway': {
      // A reel unit torn off and lying askew at the back, another still on, one flung aside.
      p.rect(2, 9, 11, 5, blade);
      p.rect(1, 8, 2, 7, p.body(1));
      p.rect(12, 8, 2, 7, p.body(2));
      wheel(p, 11, 14, 4, 9);
      wheel(p, 33, 14, 4, 9);
      deck(p, 13, 14, 22, 20, 3);
      // The engine hood flung open, the engine bared, the roll bar bent over.
      p.rect(16, 15, 16, 7, K.engine);
      for (let x = 17; x < 31; x += 2) p.rect(x, 16, 1, 5, K.engineLit);
      p.rect(15, 7, 18, 6, p.body(1));
      p.rect(15, 12, 18, 1, p.body(2));
      for (let x = 17; x < 31; x += 2) p.rect(x, 8, 1, 3, p.body(2));
      p.rect(13, 4, 2, 11, K.engine);
      p.line(14, 4, 22, 2, K.engine);
      p.line(14, 5, 22, 3, K.engine);
      p.rect(20, 24, 8, 5, K.seat);
      p.rect(20, 24, 8, 1, K.seatLit);
      crack([
        [30, 25],
        [32, 28],
        [31, 31],
      ]);
      p.rect(14, 36, 12, 5, blade);
      p.rect(13, 35, 2, 6, p.body(1));
      p.rect(25, 35, 2, 6, p.body(2));
      p.line(20, 34, 20, 36, K.metalDark);
      // One reel flung aside, a front tire rolled off, a lift arm and bolts on the lawn.
      p.rect(34, 37, 11, 5, blade);
      p.rect(33, 36, 2, 6, p.body(1));
      p.rect(44, 36, 2, 6, p.body(2));
      tire(p, 41, 22, 4.4, 3.4, p.body(2)(0, 0));
      wheel(p, 9, 30, 4, 8);
      p.line(5, 40, 11, 37, K.metalDark);
      bits([
        [29, 44],
        [8, 25],
        [44, 30],
        [3, 32],
      ]);
      smoke(24, 10, 1.5);
      return;
    }
  }
}

/**
 * Paints `id` facing `dir` into frame coordinates through `put` (west is east mirrored by the
 * caller). Frame size: 28, or 48 for the Fairway Mower.
 */
export function paintMower3q(put: Put, id: MowerId, dir: Direction, paint: BodyPaint): void {
  const facing: Facing = dir === Dir.S ? 'front' : dir === Dir.N ? 'back' : 'side';
  const p = pen(put, paint);
  switch (id) {
    case 'push':
      return paintWalkBehind(p, facing, false);
    case 'selfPropelled':
      return paintWalkBehind(p, facing, true);
    case 'reel':
      return paintReel(p, facing);
    case 'tractor':
      return paintTractor(p, facing);
    case 'zeroTurn':
      return paintZeroTurn(p, facing);
    case 'fairway':
      return paintFairway(p, facing);
  }
}
